import { HttpClient } from '@angular/common/http';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Subscription, finalize, takeUntil } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { IEnvironment } from '../../../models/environment.model';
import { FormStateService } from '../../../services/formly/form-state.service';
import { GenericInputStatusEnum } from '../../../models/irembo-generic-input-status.enum';
import { EIremboFormlyFormStateKeys } from '../../../models/irembo-formly-formstate-keys.enum';
import {
  updateUrlWithApiGatewayBaseUrl,
  handleErrorMapping,
  findFormControl,
  findFormControlInScope,
  getPopulateReferenceForm,
} from '../../../../utils/utils/data-fetch-widget-utils';
import {
  InputFieldConfig,
  MultiSelectDataFetchConfig,
  MultiSelectTableColumn,
  PreviewMetadataConfig,
} from '../../../models/multi-select-data-fetch.model';
import { getParentRepeaterKeyAndItemIndex } from '../../../../utils/utils/formly.utils';
import { IPreFetchCheckConfig } from '../../../models/generic-data-fetch-pre-pipeline.model';
import { PreFetchCheckService } from '../../../services/pre-fetch-check/pre-fetch-check.service';

/**
 * Generic Multi-Select Data Fetch Component
 * Allows users to fetch data via configurable input fields, then select multiple items from results
 */
@Component({
  standalone: false,
  selector: 'irembogov-custom-multi-select-data-fetch',
  templateUrl: './custom-multi-select-data-fetch.component.html',
  styleUrls: ['./custom-multi-select-data-fetch.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomMultiSelectDataFetchComponent
  extends FieldType<FieldTypeConfig>
  implements OnInit, AfterViewInit, OnDestroy
{
  // Dynamic form controls for input fields
  inputForm: FormGroup = new FormGroup({});
  inputControls: Map<string, FormControl> = new Map();

  // Table data and selection
  tableData: Record<string, any>[] = [];
  selectedItems = new Set<string>();
  allSelected = false;

  // Component state
  statusClass: string | undefined;
  isLoading = false;
  hasSearched = false;
  errorMessage: string | null = null;

  // Pagination
  currentPage = 0;
  pageSize = 10;
  totalElements = 0;
  totalPages = 0;

  // RxJS subjects and subscriptions
  private destroy$ = new Subject<void>();
  private subscriptions = new Subscription();
  private componentId: string;
  private environment: IEnvironment;

  // Repeater context
  private isInRepeater = false;
  private currentRepeaterForm: any;

  constructor(
    private http: HttpClient,
    private cd: ChangeDetectorRef,
    protected formStateService: FormStateService,
    private translateService: TranslateService,
    private preFetchCheckService: PreFetchCheckService,
    @Inject('environment') environment: IEnvironment
  ) {
    super();
    this.environment = environment;
    this.componentId = uuidv4();
  }

  ngOnInit(): void {
    this.initializeInputFields();
    this.initializeFieldValue();
  }

  ngAfterViewInit(): void {
    this.initializeRepeaterContext();
    this.attemptPrefetch();
  }

  private initializeRepeaterContext(): void {
    const repeaterKeys = getParentRepeaterKeyAndItemIndex(this.field);
    if (repeaterKeys) {
      this.isInRepeater = true;
      this.currentRepeaterForm = this.form;
    } else {
      this.isInRepeater = false;
      this.currentRepeaterForm = getPopulateReferenceForm(
        this.field,
        this.form
      );
    }
  }

  private findSourceControl(
    targetKey: string,
    searchInRootForm?: boolean
  ): AbstractControl | null {
    if (searchInRootForm === true) {
      return findFormControl(this.currentRepeaterForm, targetKey);
    }
    if (searchInRootForm === false && this.isInRepeater) {
      return findFormControlInScope(this.currentRepeaterForm, targetKey);
    }
    return this.isInRepeater
      ? findFormControlInScope(this.currentRepeaterForm, targetKey)
      : findFormControl(this.currentRepeaterForm, targetKey);
  }

  private attemptPrefetch(): void {
    if (!this.field.props?.['usePrefetch']) {
      return;
    }

    // Don't prefetch if there's already a value (e.g., restoring from saved state)
    if (
      Array.isArray(this.formControl.value) &&
      this.formControl.value.length > 0
    ) {
      return;
    }

    const inputFields: InputFieldConfig[] = this.config.inputFields;
    const hasSourceMappings = inputFields.some(f => f.sourceField);
    if (!hasSourceMappings) {
      return;
    }

    let allResolved = true;
    for (const fieldConfig of inputFields) {
      if (!fieldConfig.sourceField) {
        // No source mapping — use existing default/current value
        const control = this.inputControls.get(fieldConfig.key);
        if (fieldConfig.required && !control?.value) {
          allResolved = false;
          break;
        }
        continue;
      }

      const sourceControl = this.findSourceControl(
        fieldConfig.sourceField,
        fieldConfig.searchInRootForm
      );
      const sourceValue = sourceControl?.value;

      if (
        sourceValue !== null &&
        sourceValue !== undefined &&
        sourceValue !== ''
      ) {
        const control = this.inputControls.get(fieldConfig.key);
        if (control) {
          control.setValue(sourceValue, { emitEvent: false });
        }
      } else if (fieldConfig.required) {
        allResolved = false;
        break;
      }
    }

    if (allResolved && this.isFormValid) {
      this.runPipelineThenFetch();
    }
  }

  /**
   * Get the component configuration
   */
  get config(): MultiSelectDataFetchConfig {
    return {
      inputFields: this.field.props?.['inputFields'] || [],
      columns: this.field.props?.['columns'] || [],
      selectionKey: this.field.props?.['selectionKey'] || 'id',
      tableTitle: this.field.props?.['tableTitle'] || 'Results',
      tableDescription:
        this.field.props?.['tableDescription'] ||
        'Please select items before continuing.',
      emptyMessage: this.field.props?.['emptyMessage'] || 'No results found.',
      validateButtonLabel:
        this.field.props?.['validateButtonLabel'] || 'Validate',
      previewMetadata: this.field.props?.['previewMetadata'] || [],
    };
  }

  /**
   * Initialize dynamic input fields from configuration
   */
  private initializeInputFields(): void {
    const inputFields: InputFieldConfig[] = this.config.inputFields;

    inputFields.forEach(fieldConfig => {
      const validators = fieldConfig.required ? [Validators.required] : [];
      if (fieldConfig.pattern) {
        validators.push(Validators.pattern(fieldConfig.pattern));
      }
      if (fieldConfig.minLength) {
        validators.push(Validators.minLength(fieldConfig.minLength));
      }
      if (fieldConfig.maxLength) {
        validators.push(Validators.maxLength(fieldConfig.maxLength));
      }

      const control = new FormControl(
        fieldConfig.defaultValue || '',
        validators
      );
      this.inputControls.set(fieldConfig.key, control);
      this.inputForm.addControl(fieldConfig.key, control);
    });
  }

  /**
   * Initialize field value if it exists
   */
  private initializeFieldValue(): void {
    const currentValue = this.formControl.value;
    if (Array.isArray(currentValue) && currentValue.length > 0) {
      const selectionKey = this.config.selectionKey;
      currentValue.forEach(item => {
        const key = item[selectionKey];
        if (key) {
          this.selectedItems.add(key);
        }
      });
    }
  }

  /**
   * Get input field configurations
   */
  get inputFields(): InputFieldConfig[] {
    return this.config.inputFields;
  }

  /**
   * Check if the form inputs are valid
   */
  get isFormValid(): boolean {
    return this.inputForm.valid;
  }

  /**
   * Search for data when user clicks Validate button
   */
  searchData(): void {
    if (!this.isFormValid) {
      this.errorMessage = this.translateService.instant(
        'Please fill in all required fields'
      );
      this.cd.markForCheck();
      return;
    }

    // Clear previous selections when searching
    this.clearSelections();
    this.formStateService.clearGlobalError(this.field);

    this.runPipelineThenFetch();
  }

  /**
   * Run pre-fetch checks (if configured), then execute the main data fetch.
   */
  private runPipelineThenFetch(): void {
    const preFetchChecks: IPreFetchCheckConfig[] =
      this.field.props?.['preFetchChecks'] ?? [];
    const enabledChecks = preFetchChecks.filter(check => check.enabled);

    if (enabledChecks.length === 0) {
      this.fetchData();
      return;
    }

    this.clearPipelineErrors();
    this.isLoading = true;
    this.statusClass = GenericInputStatusEnum.FETCHING;
    this.errorMessage = null;
    this.cd.markForCheck();

    const inputValue = this.getPrimaryInputValue();

    this.preFetchCheckService
      .executeChecks(
        enabledChecks,
        inputValue,
        (sourceField, searchInRootForm) => {
          // Check internal input controls first, then parent form
          const internalControl = this.inputControls.get(sourceField);
          if (internalControl) {
            return internalControl.value;
          }
          return this.findSourceControl(sourceField, searchInRootForm)?.value;
        },
        this.environment
      )
      .then(result => {
        if (result.passed) {
          this.fetchData();
        } else {
          this.isLoading = false;
          this.setPipelineError(
            result.errorKey ?? 'preFetchCheckFailed',
            result.errorMessage
          );
          this.cd.markForCheck();
        }
      })
      .catch(() => {
        this.isLoading = false;
        this.statusClass = GenericInputStatusEnum.DANGER;
        this.errorMessage = this.translateService.instant(
          'An error occurred during validation checks'
        );
        this.cd.markForCheck();
      });
  }

  private getPrimaryInputValue(): string {
    const firstInput = this.config.inputFields[0];
    if (firstInput) {
      return this.inputControls.get(firstInput.key)?.value ?? '';
    }
    return '';
  }

  private clearPipelineErrors(): void {
    const preFetchChecks: IPreFetchCheckConfig[] =
      this.field.props?.['preFetchChecks'] ?? [];
    for (const check of preFetchChecks) {
      if (check.errorKey && this.formControl.errors?.[check.errorKey]) {
        const errors = { ...this.formControl.errors };
        delete errors[check.errorKey];
        this.formControl.setErrors(
          Object.keys(errors).length > 0 ? errors : null
        );
      }
    }
    this.formControl.updateValueAndValidity();
    this.formStateService.clearGlobalError(this.field);
    this.errorMessage = null;
  }

  private setPipelineError(errorKey: string, message?: string): void {
    this.statusClass = GenericInputStatusEnum.DANGER;

    const resolvedMessage =
      message ||
      this.field.validation?.messages?.[errorKey] ||
      'Validation check failed';
    this.errorMessage = this.translateService.instant(String(resolvedMessage));

    const errors: Record<string, { message: string }> = {
      ...this.formControl.errors,
      [errorKey]: { message: this.errorMessage },
    };
    this.formControl.setErrors(errors);

    if (this.field?.props?.['showGlobalError'] && this.errorMessage) {
      this.formStateService.addGlobalError(this.field, this.errorMessage);
    }

    this.tableData = [];
  }

  /**
   * Handle pagination change
   */
  onPageChange(event: any): void {
    if (event.page) {
      this.currentPage = event.page - 1;
      this.pageSize = event.pageSize || this.pageSize;
      this.cd.markForCheck();
    }
  }

  /**
   * Fetch data from the API
   */
  private fetchData(): void {
    let { url } = this.field.props || {};
    const { endpointCode } = this.field.props || {};

    if (!url || !endpointCode) {
      this.handleError({
        error: { message: 'URL and endpointCode are required in field props' },
      });
      return;
    }

    if (this.field.props?.['useBaseUrl']) {
      url = updateUrlWithApiGatewayBaseUrl(url, this.environment);
    }

    const payload = this.buildPayload();

    const params = {
      endpointCode,
      callerId: uuidv4(),
      payload,
      requester: uuidv4(),
    };

    this.isLoading = true;
    this.hasSearched = true;
    this.statusClass = GenericInputStatusEnum.FETCHING;
    this.errorMessage = null;
    this.formControl.setErrors(null);
    this.cd.markForCheck();

    this.subscriptions.add(
      this.http
        .post<any>(url, params)
        .pipe(
          finalize(() => {
            this.isLoading = false;
            this.cd.markForCheck();
          }),
          takeUntil(this.destroy$)
        )
        .subscribe({
          next: response => this.handleFetchSuccess(response),
          error: error => this.handleError(error),
        })
    );
  }

  /**
   * Build the payload for the API request from input fields
   */
  private buildPayload(): Record<string, any> {
    const { payloadKeyMappings, useMultiplePayloadKeys } =
      this.field.props || {};
    const payload: Record<string, any> = {};

    // If using payload key mappings
    if (useMultiplePayloadKeys && Array.isArray(payloadKeyMappings)) {
      payloadKeyMappings.forEach((mapping: any) => {
        if (mapping.isStatic && mapping.staticValue !== undefined) {
          payload[mapping.key] = mapping.staticValue;
        } else if (mapping.sourceField) {
          const control = this.inputControls.get(mapping.sourceField);
          if (control) {
            payload[mapping.key || mapping.sourceField] = control.value?.trim();
          }
        }
      });
      return payload;
    }

    // Default: use input field values directly
    this.inputControls.forEach((control, key) => {
      payload[key] = control.value?.trim();
    });

    return payload;
  }

  /**
   * Handle successful API response
   */
  private handleFetchSuccess(response: any): void {
    try {
      this.statusClass = GenericInputStatusEnum.SUCCESS;

      const data = response?.data;
      const responseData = data?.response;

      let items: any[] = [];

      // Parse response - support both string and object responses
      if (typeof responseData === 'string') {
        try {
          const parsed = JSON.parse(responseData);
          items = this.extractDataFromResponse(parsed);
          this.updatePaginationFromResponse(parsed);
        } catch (parseError) {
          console.error('Error parsing response data:', parseError);
          items = [];
        }
      } else if (responseData) {
        items = this.extractDataFromResponse(responseData);
        this.updatePaginationFromResponse(responseData);
      }

      // Transform items to table data
      this.tableData = this.transformToTableData(items);

      this.formControl.setErrors(null);
      this.errorMessage = null;

      this.formStateService?.emitFetchSuccess?.({
        fetchKey: this.field.key as string,
        componentId: this.componentId,
        response,
      });

      this.cd.markForCheck();
    } catch (error) {
      console.error('Error processing fetch response:', error);
      this.handleError({
        error: { message: 'Error processing server response' },
      });
    }
  }

  /**
   * Extract data array from response using configured path
   */
  private extractDataFromResponse(response: any): any[] {
    const dataPath = this.field.props?.['dataPath'] || 'data.content';
    return this.getNestedValue(response, dataPath) || [];
  }

  /**
   * Update pagination info from response
   */
  private updatePaginationFromResponse(response: any): void {
    const paginationConfig = this.field.props?.['paginationConfig'] || {};
    const pageablePath = paginationConfig.pageablePath || 'data.pageable';
    const totalElementsPath =
      paginationConfig.totalElementsPath || 'data.totalElements';
    const totalPagesPath = paginationConfig.totalPagesPath || 'data.totalPages';

    const pageable = this.getNestedValue(response, pageablePath);
    if (pageable) {
      this.currentPage = pageable.pageNumber || 0;
      this.pageSize = pageable.pageSize || 10;
    }

    this.totalElements = this.getNestedValue(response, totalElementsPath) || 0;
    this.totalPages = this.getNestedValue(response, totalPagesPath) || 0;
  }

  /**
   * Transform raw data to table display format based on column configuration
   */
  private transformToTableData(items: any[]): Record<string, any>[] {
    if (!Array.isArray(items)) {
      return [];
    }

    const columns = this.getColumnConfiguration();
    const selectionKey = this.config.selectionKey;

    return items.map(item => {
      const rowData: Record<string, any> = {};

      // Always include the selection key
      rowData[selectionKey] = this.getNestedValue(item, selectionKey);

      // Map each column
      columns.forEach((column: MultiSelectTableColumn) => {
        const key = column.key;
        const value = this.getNestedValue(item, column.dataPath || key);

        // Apply formatter if configured
        if (column.formatter) {
          rowData[key] = this.applyFormatter(value, column.formatter);
        } else {
          rowData[key] = value;
        }
      });

      // Store original item for metadata extraction
      rowData['_originalItem'] = item;
      rowData['selected'] = this.selectedItems.has(rowData[selectionKey]);

      return rowData;
    });
  }

  /**
   * Apply formatter to value
   */
  private applyFormatter(value: any, formatter: string): any {
    switch (formatter) {
      case 'date':
        return this.formatDate(value);
      case 'currency':
        return value;
      case 'array-first-name':
        return Array.isArray(value) && value.length > 0
          ? value[0]?.name
          : 'N/A';
      default:
        return value;
    }
  }

  /**
   * Get nested value from object using dot notation
   */
  private getNestedValue(obj: any, path: string): any {
    if (!path) return obj;
    return path.split('.').reduce((current, prop) => current?.[prop], obj);
  }

  /**
   * Format date string for display
   */
  private formatDate(dateString: string | null | undefined): string {
    if (!dateString) return 'N/A';

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;

      return `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date
        .getDate()
        .toString()
        .padStart(2, '0')}/${date.getFullYear()}`;
    } catch {
      return dateString;
    }
  }

  /**
   * Handle API errors
   */
  private handleError(error: any): void {
    const { errors, statusClass } = handleErrorMapping(
      error?.error || error,
      this.field.props?.['errorMapping'],
      'fetchError',
      this.field,
      this.formControl,
      true
    );

    const errorKey = Object.keys(errors)[0];
    let errorMessage =
      errors[errorKey]?.message ||
      this.field.validation?.messages?.[errorKey] ||
      'An error occurred while fetching data';

    // Handle function-based validation messages and translate
    if (typeof errorMessage === 'function') {
      errorMessage = errorMessage();
    }
    this.errorMessage = this.translateService.instant(String(errorMessage));

    this.statusClass = statusClass || GenericInputStatusEnum.DANGER;
    this.formControl.setErrors(errors);

    if (this.field?.props?.['showGlobalError'] && this.errorMessage) {
      this.formStateService.addGlobalError(this.field, this.errorMessage);
    }

    this.tableData = [];
    this.cd.markForCheck();
  }

  /**
   * Toggle selection for a specific item
   */
  toggleItemSelection(item: Record<string, any>): void {
    const selectionKey = this.config.selectionKey;
    const itemKey = item[selectionKey];

    if (this.selectedItems.has(itemKey)) {
      this.selectedItems.delete(itemKey);
      item['selected'] = false;
    } else {
      this.selectedItems.add(itemKey);
      item['selected'] = true;
    }

    this.updateFieldValue();
    this.updateAllSelectedState();
    this.cd.markForCheck();
  }

  /**
   * Toggle selection for all items
   */
  toggleAllSelection(): void {
    const selectionKey = this.config.selectionKey;

    if (this.allSelected) {
      this.clearSelections();
    } else {
      this.tableData.forEach(item => {
        this.selectedItems.add(item[selectionKey]);
        item['selected'] = true;
      });
      this.allSelected = true;
    }

    this.updateFieldValue();
    this.cd.markForCheck();
  }

  /**
   * Clear all selections
   */
  private clearSelections(): void {
    this.selectedItems.clear();
    this.tableData.forEach(item => (item['selected'] = false));
    this.allSelected = false;
    this.updateFieldValue();
  }

  /**
   * Update the all selected checkbox state
   */
  private updateAllSelectedState(): void {
    this.allSelected =
      this.tableData.length > 0 &&
      this.tableData.every(item => item['selected']);
  }

  /**
   * Get column configuration for the data table display
   */
  getColumnConfiguration(): MultiSelectTableColumn[] {
    const columns = this.config.columns;

    if (!columns || !Array.isArray(columns) || columns.length === 0) {
      throw new Error(
        'Multi-select data fetch requires "columns" configuration in field props'
      );
    }

    return columns;
  }

  /**
   * Update the formly field value with selected item objects
   */
  private updateFieldValue(): void {
    const selectionKey = this.config.selectionKey;

    // Collect internal input values for preview metadata
    const inputValues: Record<string, any> = {};
    this.inputControls.forEach((control, key) => {
      inputValues[key] = control.value;
    });

    // Get full item objects for selected items
    const selectedItemObjects = this.tableData
      .filter(item => this.selectedItems.has(item[selectionKey]))
      .map(item => {
        // Return the row data without internal properties
        const { ...cleanItem } = item;

        // Add __inputValues to each item for preview metadata access
        return {
          ...cleanItem,
          __inputValues: inputValues,
        };
      });

    // Save to form control
    this.formControl.setValue(selectedItemObjects);
    this.formControl.updateValueAndValidity();

    // Save to form state (without __inputValues for clean data)
    const cleanSelectedItems = selectedItemObjects.map(item => {
      const { ...cleanItem } = item;
      return cleanItem;
    });

    this.formStateService.saveFetchedDataKeyInFormState(
      this.field,
      cleanSelectedItems,
      EIremboFormlyFormStateKeys.FETCHED_DATA,
      false
    );

    // Save preview metadata
    const previewMetadata = this.config.previewMetadata;
    if (previewMetadata && previewMetadata.length > 0) {
      const metadata = this.computePreviewMetadata(selectedItemObjects);
      this.formStateService.saveFetchedDataKeyInFormState(
        this.field,
        metadata,
        EIremboFormlyFormStateKeys.FETCHED_METADATA,
        false
      );
    }

    // Clear validation error if selections are made
    if (
      selectedItemObjects.length > 0 &&
      this.formControl.errors?.['notVerifiedByTriggerAction']
    ) {
      const { ...otherErrors } = this.formControl.errors;
      this.formControl.setErrors(
        Object.keys(otherErrors).length > 0 ? otherErrors : null
      );
    }
  }

  /**
   * Compute preview metadata from selected items
   */
  private computePreviewMetadata(selectedItems: any[]): Record<string, any> {
    const metadata: Record<string, any> = {};
    const previewConfig = this.config.previewMetadata || [];

    previewConfig.forEach((config: PreviewMetadataConfig) => {
      const sourceField = config.sourceField || '';

      switch (config.type) {
        case 'count':
          metadata[config.key] = selectedItems.length;
          break;
        case 'sum':
          metadata[config.key] = selectedItems.reduce((sum, item) => {
            return sum + (this.getNestedValue(item, sourceField) || 0);
          }, 0);
          break;
        case 'first':
          metadata[config.key] =
            selectedItems.length > 0
              ? this.getNestedValue(selectedItems[0], sourceField)
              : null;
          break;
        case 'input': {
          const control = this.inputControls.get(sourceField);
          metadata[config.key] = control?.value || null;
          break;
        }
        case 'static':
          metadata[config.key] = config.value;
          break;
      }
    });

    return metadata;
  }

  /**
   * Format currency for display
   */
  formatCurrency(amount: number): string {
    return `${amount.toLocaleString()} RWF`;
  }

  /**
   * Get formatted value for display based on column configuration
   */
  getFormattedValue(
    item: Record<string, any>,
    column: MultiSelectTableColumn
  ): string {
    const value = item[column.key];

    if (column.formatter === 'currency' && typeof value === 'number') {
      return this.formatCurrency(value);
    }

    return value != null ? String(value) : 'N/A';
  }

  /**
   * Get the count of selected items
   */
  get selectedCount(): number {
    return this.selectedItems.size;
  }

  /**
   * Check if the form is disabled
   */
  get isDisabled(): boolean {
    return this.field.props?.['disabled'] || false;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.subscriptions.unsubscribe();
  }
}
